import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { api } from '../lib/api';
import { formatDistance, getPosition } from '../lib/menu';

const { FiMapPin, FiNavigation, FiLoader, FiPhone } = FiIcons;

// Shown instead of the menu when the location lock is on and the visitor has
// no pass yet. A successful check sets a 6 hour pass cookie on the server.
export default function LocationGate({ info, onUnlocked }) {
  const [state, setState] = useState({ step: 'idle' });
  const venue = info.venue || {};
  const name = venue.name || 'The Stonewall Inn';

  const check = async () => {
    setState({ step: 'locating' });
    let pos;
    try {
      pos = await getPosition();
    } catch (err) {
      // GeolocationPositionError: 1 denied, 2 unavailable, 3 timeout
      setState({ step: err.code === 1 ? 'denied' : err.code === 'unsupported' ? 'unsupported' : 'unavailable' });
      return;
    }
    try {
      const result = await api.post('geo/check', {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
      if (result.ok) onUnlocked();
      else setState({ step: 'outside', distance: result.distanceFeet });
    } catch (err) {
      setState({ step: 'error', message: err.message });
    }
  };

  // If location was already allowed for this site, check without a tap.
  useEffect(() => {
    navigator.permissions?.query({ name: 'geolocation' })
      .then((p) => p.state === 'granted' && check())
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = state.step === 'locating';
  const messages = {
    denied: 'Location access is blocked. Allow location for this site in your browser settings, then try again.',
    unavailable: "We couldn't get your location. Make sure location services are on, then try again.",
    unsupported: "This browser can't share your location. Try another browser on your phone.",
    outside: `You're about ${formatDistance(state.distance || 0)} away. Come on by and check the menu from the bar!`,
    error: state.message,
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-900">
      <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center space-y-5">
        {venue.logoUrl && <img src={venue.logoUrl} alt={`${name} logo`} className="w-20 h-20 rounded-full object-cover mx-auto" />}
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{name}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Our drink menu is available to guests at the bar. Share your location to open it.
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Within {formatDistance(info.radiusFeet || 2000)} of 53 Christopher St. Your location is only used for this check.
          </p>
        </div>

        <button
          onClick={check}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
        >
          <SafeIcon icon={busy ? FiLoader : FiNavigation} className={`w-5 h-5 ${busy ? 'animate-spin' : ''}`} />
          {busy ? 'Checking your location...' : state.step === 'idle' ? 'Share my location' : 'Try again'}
        </button>

        {messages[state.step] && (
          <p role="status" className={`text-sm ${state.step === 'outside' ? 'text-gray-700 dark:text-gray-200' : 'text-red-600 dark:text-red-400'}`}>
            {messages[state.step]}
          </p>
        )}

        <div className="flex justify-center gap-3 pt-1">
          {venue.mapsUrl && (
            <a href={venue.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-500 hover:bg-blue-600 text-white">
              <SafeIcon icon={FiMapPin} className="w-4 h-4" /> Directions
            </a>
          )}
          {venue.phone && (
            <a href={`tel:${venue.phone}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-green-500 hover:bg-green-600 text-white">
              <SafeIcon icon={FiPhone} className="w-4 h-4" /> Call
            </a>
          )}
        </div>

        <a href="/admin" className="block text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">Staff sign in</a>
      </div>
    </div>
  );
}
